import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, of, shareReplay, tap } from 'rxjs';
import { environment } from '../../environments/environment';

/** The signed-in staff member's granted V2 function codes (`GET api/Staff/me/permissions`). */
export interface MyPermissions {
  /** True when the caller is in the Superadmin usergroup — every function is allowed. */
  isSuperAdmin: boolean;
  functionCodes: string[];
}

interface Envelope<T> { status: boolean; data: T }

/**
 * Frontend half of the V2 user-group permission system. Loads the caller's granted function
 * codes once per session and exposes `has(code)` for guards, directives and components.
 *
 * UX only — the backend `[RequiresFunction]` filter is the actual enforcement (403). A failed
 * load degrades to "no permissions" (guards redirect, buttons block), never to "allow all".
 */
@Injectable({ providedIn: 'root' })
export class PermissionService {
  private readonly http = inject(HttpClient);
  private readonly apiBase = `${environment.apiBaseUrl}/api/Staff`;

  private readonly permissions = signal<MyPermissions | null>(null);
  private load$: Observable<MyPermissions> | null = null;

  /** True once permissions have been fetched (or the fetch failed and defaulted to none). */
  readonly loaded = computed(() => this.permissions() !== null);
  readonly isSuperAdmin = computed(() => this.permissions()?.isSuperAdmin ?? false);

  private readonly codeSet = computed(
    () => new Set((this.permissions()?.functionCodes ?? []).map((c) => c.toLowerCase()))
  );

  /** Signal read for templates: whether the caller's usergroup is granted the function code. */
  has(code: string): boolean {
    const p = this.permissions();
    if (!p) return false;
    return p.isSuperAdmin || this.codeSet().has(code.toLowerCase());
  }

  /**
   * Resolves the permissions, fetching them on first call and replaying the cached result after.
   * Guards await this; templates can use `has()` once `loaded()` is true.
   */
  ensureLoaded(): Observable<MyPermissions> {
    const current = this.permissions();
    if (current) return of(current);

    this.load$ ??= this.http.get<Envelope<MyPermissions>>(`${this.apiBase}/me/permissions`).pipe(
      map((r) => r.data ?? { isSuperAdmin: false, functionCodes: [] }),
      tap({
        next: (p) => this.permissions.set(p),
        // Fail closed: no permissions rather than a broken page pretending access exists.
        error: () => this.permissions.set({ isSuperAdmin: false, functionCodes: [] }),
      }),
      shareReplay(1)
    );
    return this.load$;
  }

  /** Drops the cached set so the next `ensureLoaded()` refetches (e.g. after grants change). */
  refresh(): void {
    this.permissions.set(null);
    this.load$ = null;
  }
}
